import React, { useCallback, useLayoutEffect, useRef } from "react";
import { twMerge } from "tailwind-merge";
import { weightClassesName } from "../constants";
import { splitWeightClassLabel } from "../utils/common";

/**
 * Pick a weight class.
 *
 * A tappable list rather than a native <select>: the eleven classes are a short,
 * fixed set, and seeing them laid out makes the neighbouring classes obvious —
 * which is the thing a fighter or admin is actually deciding between.
 *
 * Controlled: the parent owns `value` (a class key, A–K) and gets `onChange(key)`.
 *
 * @param {string|null} value
 * @param {(weightClass: string) => void} onChange
 * @param {string|null} [baselineClass] the previously recorded class, marked so
 *   a change is visible as a change rather than just a new selection.
 * @param {boolean} [disabled]
 */
export default function WeightClassPicker({
    value,
    onChange,
    baselineClass = null,
    disabled = false,
    className,
}) {
    const moved = !!baselineClass && !!value && baselineClass !== value;

    const listRef = useRef(null);
    const selectedRef = useRef(null);
    // Set when the change came from a click inside this list, so picking a row
    // near the edge doesn't yank the list out from under the cursor.
    const pickedHereRef = useRef(false);

    // Eleven classes don't fit the panel, so opening at the top hides the class
    // the fighter is actually in. Centre it instead — but scroll the list
    // element itself rather than scrollIntoView(), which would also drag the
    // modal and the page behind it.
    const centerSelected = useCallback(() => {
        const list = listRef.current;
        const item = selectedRef.current;
        if (!list || !item) return false;

        const listBox = list.getBoundingClientRect();
        const itemBox = item.getBoundingClientRect();
        // Not laid out yet (still hidden): nothing meaningful to measure.
        if (listBox.height === 0) return false;
        // Already on screen — a scroll here would be movement for its own sake.
        if (itemBox.top >= listBox.top && itemBox.bottom <= listBox.bottom) {
            return true;
        }

        const offsetWithinList = itemBox.top - listBox.top + list.scrollTop;
        list.scrollTop = Math.max(
            0,
            offsetWithinList - (listBox.height - itemBox.height) / 2
        );
        return true;
    }, []);

    useLayoutEffect(() => {
        if (pickedHereRef.current) {
            pickedHereRef.current = false;
            return;
        }
        if (centerSelected()) return;
        // Mounted hidden (a dialog opened in the same tick): try once more
        // after the browser has laid it out.
        const frame = requestAnimationFrame(centerSelected);
        return () => cancelAnimationFrame(frame);
    }, [value, centerSelected]);

    return (
        <div className={twMerge("w-full flex flex-col gap-2", className)}>
            <div
                ref={listRef}
                // 24rem shows ~6 of the 11 classes. Capped against the
                // viewport too, because .modal-box is itself limited to
                // 100vh-5em — a taller list than the phone can hold would
                // put a second scrollbar inside the first.
                className="flex flex-col gap-1 max-h-[min(24rem,50vh)] overflow-y-auto"
                role="radiogroup"
                aria-label="Weight class"
            >
                {Object.keys(weightClassesName).map((key) => {
                    const selected = value === key;
                    const wasBaseline = baselineClass === key && moved;
                    const { lb, kg } = splitWeightClassLabel(key);
                    return (
                        <button
                            key={key}
                            ref={selected ? selectedRef : undefined}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            disabled={disabled}
                            onClick={() => {
                                // Only when this is a real change — otherwise
                                // the flag is never cleared by a re-render and
                                // would swallow the next external recentre.
                                if (!selected) pickedHereRef.current = true;
                                onChange?.(key);
                            }}
                            className={twMerge(
                                "w-full flex flex-row items-center gap-3 border border-base-300 px-3 py-2 text-left",
                                "hover:bg-base-200 disabled:opacity-50 disabled:cursor-not-allowed",
                                selected &&
                                    "bg-white text-black border-white hover:bg-white",
                                wasBaseline && !selected && "border-base-content/40"
                            )}
                        >
                            <span
                                className={twMerge(
                                    "font-mono font-bold w-5 shrink-0",
                                    selected ? "text-black" : "opacity-60"
                                )}
                            >
                                {key}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="font-semibold block truncate">
                                    {weightClassesName[key]}
                                </span>
                                {/* The kg figure is the number people weigh
                                    themselves against, so it gets the size and
                                    weight; the pound equivalent stays quiet. */}
                                <span className="block truncate">
                                    {lb && (
                                        <span
                                            className={twMerge(
                                                "text-xs",
                                                selected
                                                    ? "opacity-70"
                                                    : "opacity-60"
                                            )}
                                        >
                                            {lb}{" "}
                                        </span>
                                    )}
                                    {kg && (
                                        <span
                                            className={twMerge(
                                                "text-sm font-bold tabular-nums",
                                                selected
                                                    ? "opacity-100"
                                                    : "opacity-90"
                                            )}
                                        >
                                            ({kg})
                                        </span>
                                    )}
                                </span>
                            </span>
                            {wasBaseline && !selected && (
                                <span className="badge badge-sm badge-ghost shrink-0">
                                    Current
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {moved && (
                <p className="text-xs text-warning">
                    Moving from {baselineClass} {weightClassesName[baselineClass]}{" "}
                    to {value} {weightClassesName[value]}.
                </p>
            )}
        </div>
    );
}
